import type { CSSProperties } from 'react';
import type { TFunction } from 'i18next';
import { Tag } from 'antd';

export type TransportStatusView = 'all' | 'inProgress' | 'completed' | 'planned' | 'custom';
export type TransportStatusDisplay = 'planned' | 'inProgress' | 'completed' | 'cancelled';

export const TRANSPORT_STATUS_VIEW_GROUPS: Record<
  Exclude<TransportStatusView, 'custom' | 'all'>,
  string[]
> = {
  // Older detailed states deliberately resolve into the same operator view.
  // The transport workspace has just three active stages: planned, in progress
  // and completed. This keeps legacy rows discoverable without exposing the
  // old, overly granular status model in controls.
  inProgress: ['LOADING', 'IN_TRANSIT', 'DELAYED'],
  completed: ['DELIVERED'],
  planned: ['PENDING', 'PLANNED', 'SCHEDULED'],
};

export function getTransportStatusDisplay(status: string): TransportStatusDisplay {
  if (['PENDING', 'PLANNED', 'SCHEDULED'].includes(status)) return 'planned';
  if (['LOADING', 'IN_TRANSIT', 'DELAYED'].includes(status)) return 'inProgress';
  if (status === 'DELIVERED') return 'completed';
  return 'cancelled';
}

export function getTransportStatusDisplayTranslationKey(status: string): string {
  return `transport:status.${getTransportStatusDisplay(status)}`;
}

export function detectTransportStatusView(statusFilter: string[]): TransportStatusView {
  if (statusFilter.length === 0) return 'all';

  const sortedFilter = [...statusFilter].sort().join(',');

  for (const view of Object.keys(TRANSPORT_STATUS_VIEW_GROUPS) as Array<
    keyof typeof TRANSPORT_STATUS_VIEW_GROUPS
  >) {
    const groupKey = [...TRANSPORT_STATUS_VIEW_GROUPS[view]].sort().join(',');
    if (sortedFilter === groupKey) return view;
  }

  return 'custom';
}

// Single home for the "consolidated vs single-order" transport badge, used by
// both the transports list (route cell) and the transport detail page (card
// header). A transport carrying 2+ orders reads "Zbiorczy · N zam.", one order
// reads "Pojedynczy" — kept in one place so the two pages can never drift.
export function renderTransportTypeTag(
  t: TFunction,
  orderCount: number,
  style?: CSSProperties,
): React.ReactNode {
  if (orderCount >= 2) {
    return (
      <Tag color="blue" bordered={false} style={style}>
        {t('transport:type.consolidatedWithCount', { count: orderCount })}
      </Tag>
    );
  }

  return (
    <Tag color="default" bordered={false} style={style}>
      {t('transport:type.single')}
    </Tag>
  );
}

// MED (i18n error mapping): the backend's transport-mutation error messages
// are English (`error.message` in transportService.js) and are meant to stay
// that way for logs/API consumers — the FRONTEND is responsible for
// translating known, stable error CODES into Polish-first copy instead of
// echoing the raw string. Single home so create/edit/delete mutations map the
// same codes identically. Codes not covered here fall through to the
// caller's own generic fallback message.
/** One express-validator failure as returned by the routes' `checkValidation`. */
export interface TransportMutationValidatorError {
  msg?: string;
  path?: string;
  param?: string;
}

export interface TransportMutationErrorResponse {
  code?: string;
  error?: string;
  message?: string;
  fields?: string[];
  // Request-shape rejections (`400 { errors: [...] }`) carry no `code`, so
  // without this they used to fall all the way through to the caller's generic
  // "Nie udało się utworzyć transportu" — which told the operator nothing about
  // WHICH field the backend refused.
  errors?: TransportMutationValidatorError[];
  wzDocuments?: Array<{ id?: string; wzNumber?: string; orderId?: string }>;
  // Etap 2 (transport przywozowy): the inbound mirror of `wzDocuments` — PZ
  // receipts pinning this transport, returned with PZ_TRANSPORT_LINK_EXISTS.
  pzDocuments?: Array<{ id?: string; pzNumber?: string; supplierOrderId?: string }>;
}

// Up to this many validator failures are named in the toast; beyond that the
// sentence stops being readable and the operator needs the field-level errors
// on the form anyway.
const MAX_REPORTED_VALIDATION_ERRORS = 3;

/**
 * Compact field list from an express-validator response. Validator prose is
 * backend-only and may be English, so UI copy names the Polish field and asks
 * the operator to correct it instead of leaking an unstable implementation
 * detail.
 */
const TRANSPORT_VALIDATION_FIELD_KEYS: Record<string, string> = {
  orderIds: 'transport:validationFields.orders',
  supplierOrderIds: 'transport:validationFields.supplierOrders',
  transportRate: 'transport:validationFields.transportRate',
  currency: 'transport:validationFields.currency',
  loadingDate: 'transport:validationFields.loadingDate',
  estimatedDelivery: 'transport:validationFields.estimatedDelivery',
  actualDelivery: 'transport:validationFields.actualDelivery',
  loadingAddresses: 'transport:validationFields.loadingAddresses',
  deliveryAddresses: 'transport:validationFields.deliveryAddresses',
  transportCompanyId: 'transport:validationFields.transportCompany',
};

function formatValidationDetails(t: TFunction, errors: TransportMutationValidatorError[]): string {
  return errors
    .slice(0, MAX_REPORTED_VALIDATION_ERRORS)
    .map((error) => {
      const field = error.path || error.param;
      return field && TRANSPORT_VALIDATION_FIELD_KEYS[field]
        ? t(TRANSPORT_VALIDATION_FIELD_KEYS[field])
        : t('transport:validationFields.generic');
    })
    .filter(Boolean)
    .join('; ');
}

export function mapTransportMutationErrorToMessage(
  t: TFunction,
  data: TransportMutationErrorResponse | undefined | null,
): string | undefined {
  if (!data?.code) {
    // No domain code, but a request-shape rejection still says something
    // concrete — surface it instead of letting the caller fall back to its
    // generic message.
    const validationDetails = Array.isArray(data?.errors) && data.errors.length > 0
      ? formatValidationDetails(t, data.errors)
      : '';

    return validationDetails
      ? t('transport:messages.requestRejected', { details: validationDetails })
      : undefined;
  }

  if (data.code === 'MIXED_UNIT_QUANTITY_ALLOCATION') {
    return t('transport:allocation.mixedQuantityUnitsBlocked');
  }

  if (data.code === 'WZ_TRANSPORT_LINK_EXISTS') {
    const numbers = (data.wzDocuments || [])
      .map((wz) => wz.wzNumber)
      .filter(Boolean)
      .join(', ');
    return numbers
      ? t('transport:messages.wzLinkExistsWithNumbers', { numbers })
      : t('transport:messages.wzLinkExists');
  }

  // Etap 2: raised both when deleting a transport a PZ points at, and when an
  // edit would unlink the supplier order that PZ was received against. Same
  // cause, same sentence: the receipt has to go first.
  if (data.code === 'PZ_TRANSPORT_LINK_EXISTS') {
    const numbers = (data.pzDocuments || [])
      .map((pz) => pz.pzNumber)
      .filter(Boolean)
      .join(', ');
    return numbers
      ? t('transport:messages.pzLinkExistsWithNumbers', { numbers })
      : t('transport:messages.pzLinkExists');
  }

  // Etap 2: a save that would leave the transport with no link at all — neither
  // a customer nor a supplier order. The form blocks this before submitting, so
  // reaching here means the last customer order was removed elsewhere between
  // load and save; reuse the existing orphaned-transport sentence rather than
  // echoing the English backend message.
  if (data.code === 'TRANSPORT_HAS_NO_ORDERS') {
    return t('transport:errorTransportHasNoOrders');
  }

  if (data.code === 'CARGO_OVER_ALLOCATION') {
    return t('transport:cargo.overAllocatedServer');
  }

  if (data.code === 'TRANSPORT_CARGO_REQUIRED') {
    return t('transport:cargo.requiredServer');
  }

  if (data.code === 'TRANSPORT_CARGO_INVALID') {
    return t('transport:cargo.invalidServer');
  }

  if (data.code === 'TRANSPORT_SUPPLIER_ORDER_CARGO_UNMAPPED') {
    return t('transport:cargo.supplierOrderUnmappedServer');
  }

  if (data.code === 'ORDER_FINANCIAL_LIMIT_EXCEEDED') {
    return t('transport:messages.orderFinancialLimitExceeded');
  }

  if (data.code === 'TRANSPORT_SETTLEMENT_BASIS_LOCKED') {
    return t('transport:messages.settlementBasisLocked');
  }

  // Three business-rule rejections that used to arrive as a generic 500.
  if (data.code === 'TRANSPORT_STOCK_COST_RESERVED') {
    return t('transport:messages.stockCostReserved');
  }
  if (data.code === 'TRANSPORT_EXCHANGE_RATE_CONFLICT') {
    return t('transport:messages.exchangeRateConflict');
  }
  if (data.code === 'TRANSPORT_ASSIGNMENTS_CHANGED') {
    return t('transport:messages.assignmentsChanged');
  }

  if (data.code === 'TRANSPORT_ACTUAL_DELIVERY_REQUIRED') {
    return t('transport:validation.actualDeliveryRequired');
  }

  if (data.code === 'TRANSPORT_DELIVERY_QUANTITIES_REQUIRED') {
    return t('transport:deliveryConfirmation.requiredAll');
  }

  if (data.code === 'TRANSPORT_DELIVERY_QUANTITIES_INVALID') {
    return t('transport:deliveryConfirmation.invalid');
  }

  return undefined;
}
